'use client'

import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Shield,
  Lock,
  Bot,
  Menu,
  X,
  ArrowRight,
  Zap,
  Globe,
  Brain,
  Network,
  MessageCircle,
  AudioWaveform,
  FileImage,
  Eye,
  Database,
  FileCode,
  Layers,
  ShieldCheck,
  Cpu,
  HardDrive,
  Timer,
  Gauge,
  Twitter,
  Linkedin,
  CheckCircle2,
  Infinity,
  Search,
  Cog,
  TrendingUp,
  Crosshair,
  Heart,
  BarChart3,
  Scan,
  Activity,
  Smile,
  Headphones,
  MessagesSquare,
  Radar,
  GitBranch,
  Clock,
  Telescope,
  Lightbulb,
  LineChart,
} from 'lucide-react'
import ImageComponent from 'next/image'

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { name: '核心优势', href: '#advantages' },
    { name: 'Rust 架构', href: '#rust' },
    { name: '记忆系统', href: '#memory' },
    { name: '预测智能', href: '#prediction' },
    { name: '多模态', href: '#multimodal' },
    { name: '安全体系', href: '#security' },
    { name: '核心应用', href: '#usecases' },
  ]

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#0A0D14]/95 backdrop-blur-xl border-b border-[#6B8FFF]/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <ImageComponent src="/logo.png" alt="OmniNova" width={34} height={34} className="rounded-lg" />
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold tracking-tight text-white">OmniNova</span>
              <span className="text-[10px] font-mono text-[#6B8FFF]/60 tracking-wider uppercase">v2.0</span>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="px-3 py-1.5 text-zinc-500 hover:text-white transition-colors text-sm tracking-wide">
                {link.name}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Button asChild className="bg-[#335EEA] hover:bg-[#2748D4] text-white border-0 text-sm h-9 px-5">
              <a href="/claw/">快速开始 <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></a>
            </Button>
          </div>

          <button className="lg:hidden text-zinc-400 hover:text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/5 animate-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a key={link.name} href={link.href} className="text-zinc-400 hover:text-white transition-colors text-sm py-2 px-2" onClick={() => setIsMenuOpen(false)}>
                  {link.name}
                </a>
              ))}
              <div className="flex gap-2 pt-3 mt-2 border-t border-white/5">
                <Button asChild className="bg-[#335EEA] hover:bg-[#2748D4] text-white border-0 flex-1 h-9 text-sm">
                  <a href="/claw/">快速开始</a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true
          const duration = 2000
          const steps = 60
          const increment = target / steps
          let current = 0
          const timer = setInterval(() => {
            current += increment
            if (current >= target) {
              setCount(target)
              clearInterval(timer)
            } else {
              setCount(Math.floor(current))
            }
          }, duration / steps)
        }
      },
      { threshold: 0.5 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [target])

  return <span ref={ref}>{count}{suffix}</span>
}

function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0A0D14]">
      {/* Layered background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(232,90,60,0.15),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,rgba(232,90,60,0.05),transparent)]" />
        <div className="absolute inset-0 hero-grid opacity-[0.03]" />
      </div>

      {/* Floating particles */}
      <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-[#6B8FFF]/40 rounded-full animate-float-slow" />
      <div className="absolute top-1/3 right-1/3 w-0.5 h-0.5 bg-[#8BA4FF]/30 rounded-full animate-float-medium" />
      <div className="absolute bottom-1/3 left-1/3 w-0.5 h-0.5 bg-[#6B8FFF]/20 rounded-full animate-float-fast" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        <div className="text-center">
          {/* Top label */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6B8FFF]/[0.08] border border-[#6B8FFF]/20 mb-8 animate-fade-in">
            <div className="w-1.5 h-1.5 rounded-full bg-[#6B8FFF] animate-pulse" />
            <span className="text-xs font-mono tracking-widest text-[#6B8FFF]/80 uppercase">Built with Rust · Enterprise Ready</span>
          </div>

          {/* Main headline */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white mb-4 tracking-tighter leading-[0.9] animate-fade-in-up">
            Omni<span className="text-[#6B8FFF]">Nova</span>
          </h1>

          <p className="text-xl sm:text-2xl md:text-3xl font-light text-zinc-400 mb-3 tracking-tight animate-fade-in-up animation-delay-100">
            企业级安全智能体
          </p>

          <p className="text-base sm:text-lg text-zinc-600 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up animation-delay-200">
            纯 Rust 构建，比 OpenClaw 快 100 倍。<br className="hidden sm:block" />
            超强记忆系统 · 原生多模态 · 军事级安全防护
          </p>

          {/* Hero stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto mb-12 animate-fade-in-up animation-delay-300">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-b from-[#6B8FFF]/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-4 sm:p-5 rounded-xl border border-white/[0.04] bg-white/[0.02]">
                <div className="text-2xl sm:text-3xl font-black text-[#6B8FFF] font-mono">
                  <AnimatedCounter target={100} suffix="x" />
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 tracking-wide">性能提升</div>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-b from-[#6B8FFF]/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-4 sm:p-5 rounded-xl border border-white/[0.04] bg-white/[0.02]">
                <div className="text-2xl sm:text-3xl font-black text-white font-mono">
                  <AnimatedCounter target={99} suffix="%" />
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 tracking-wide">记忆召回率</div>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-b from-[#6B8FFF]/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-4 sm:p-5 rounded-xl border border-white/[0.04] bg-white/[0.02]">
                <div className="text-2xl sm:text-3xl font-black text-white font-mono">6</div>
                <div className="text-[11px] text-zinc-500 mt-1 tracking-wide">模态支持</div>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-b from-[#6B8FFF]/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-4 sm:p-5 rounded-xl border border-white/[0.04] bg-white/[0.02]">
                <div className="text-2xl sm:text-3xl font-black text-white font-mono">&lt;1<span className="text-base">ms</span></div>
                <div className="text-[11px] text-zinc-500 mt-1 tracking-wide">响应延迟</div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 animate-fade-in-up animation-delay-400">
            <Button asChild size="lg" className="bg-[#335EEA] hover:bg-[#2748D4] text-white border-0 px-8 h-12 text-sm font-medium shadow-lg shadow-[#335EEA]/20">
              <a href="/claw/">开始使用 <ArrowRight className="w-4 h-4 ml-2" /></a>
            </Button>
          </div>

          {/* Code snippet teaser */}
          <div className="mt-16 max-w-xl mx-auto animate-fade-in-up animation-delay-500">
            <div className="rounded-lg border border-white/[0.06] bg-[#111624] overflow-hidden">
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.04]">
                <div className="w-2 h-2 rounded-full bg-zinc-700" />
                <div className="w-2 h-2 rounded-full bg-zinc-700" />
                <div className="w-2 h-2 rounded-full bg-zinc-700" />
                <span className="text-[10px] font-mono text-zinc-600 ml-2">main.rs</span>
              </div>
              <pre className="p-4 text-left text-xs sm:text-sm font-mono leading-relaxed overflow-x-auto">
                <code>
                  <span className="text-zinc-600">{"// 3 行代码启动企业级安全智能体"}</span>{'\n'}
                  <span className="text-[#6B8FFF]">let</span> <span className="text-zinc-300">agent</span> <span className="text-zinc-600">=</span> <span className="text-[#8BA4FF]">OmniNova</span><span className="text-zinc-600">::</span><span className="text-zinc-300">builder</span><span className="text-zinc-600">()</span>{'\n'}
                  <span className="text-zinc-600">    .</span><span className="text-zinc-300">with_memory</span><span className="text-zinc-600">(</span><span className="text-[#8BA4FF]">InfiniteMemory</span><span className="text-zinc-600">::</span><span className="text-zinc-300">new</span><span className="text-zinc-600">())</span>{'\n'}
                  <span className="text-zinc-600">    .</span><span className="text-zinc-300">with_multimodal</span><span className="text-zinc-600">(</span><span className="text-[#8BA4FF]">AllModalities</span><span className="text-zinc-600">)</span>{'\n'}
                  <span className="text-zinc-600">    .</span><span className="text-zinc-300">with_security</span><span className="text-zinc-600">(</span><span className="text-[#8BA4FF]">EnterpriseSecurity</span><span className="text-zinc-600">::</span><span className="text-zinc-300">max</span><span className="text-zinc-600">())</span>{'\n'}
                  <span className="text-zinc-600">    .</span><span className="text-zinc-300">build</span><span className="text-zinc-600">()</span><span className="text-[#6B8FFF]">?</span><span className="text-zinc-600">;</span>
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0A0D14] to-transparent" />
    </section>
  )
}

function AdvantagesSection() {
  const advantages = [
    {
      icon: Zap,
      title: '100x 性能飞跃',
      subtitle: 'Rust 原生编译',
      description: '纯 Rust 构建的推理引擎，零 GC 停顿，零拷贝数据流转。在同等硬件条件下，吞吐量比 OpenClaw 高出 100 倍。',
      stat: '100x',
      statLabel: 'faster',
      gradient: 'from-[#335EEA] to-[#6B8FFF]',
    },
    {
      icon: Brain,
      title: '超强记忆系统',
      subtitle: '无限上下文窗口',
      description: '分层记忆架构：工作记忆 + 长期记忆 + 语义记忆。支持百万 token 级别的上下文持久化，智能体真正"记住"一切。',
      stat: '∞',
      statLabel: 'memory',
      gradient: 'from-[#6B8FFF] to-[#335EEA]',
    },
    {
      icon: Shield,
      title: '军事级安全',
      subtitle: '企业级防护体系',
      description: 'Rust 内存安全保障 + 多层 Prompt 防护 + 数据沙箱隔离。通过 SOC2、ISO27001 认证，满足最严苛的企业合规要求。',
      stat: '0',
      statLabel: 'CVE',
      gradient: 'from-[#2748D4] to-[#335EEA]',
    },
    {
      icon: Layers,
      title: '原生多模态',
      subtitle: '6 种模态统一架构',
      description: '文本、图像、语音、视频、代码、文档——六大模态原生融合，统一推理管线，跨模态零损耗切换。',
      stat: '6',
      statLabel: 'modals',
      gradient: 'from-[#8BA4FF] to-[#6B8FFF]',
    },
  ]

  return (
    <section id="advantages" className="py-28 bg-[#0A0D14] relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,90,60,0.03),transparent_70%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.06] mb-6">
            <Zap className="w-3 h-3 text-[#6B8FFF]" />
            <span className="text-xs font-mono text-zinc-500 tracking-wider uppercase">Core Advantages</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            不是改良，是<span className="text-[#6B8FFF]">颠覆</span>
          </h2>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto">
            从底层架构重新定义智能体框架的性能天花板
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {advantages.map((item, i) => (
            <div key={i} className="group relative rounded-2xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] hover:border-[#6B8FFF]/20 transition-all duration-500 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#6B8FFF]/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-7 sm:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black font-mono text-[#6B8FFF]">{item.stat}</div>
                    <div className="text-[10px] font-mono text-zinc-600 tracking-wider uppercase">{item.statLabel}</div>
                  </div>
                </div>
                <div className="text-[11px] font-mono text-[#6B8FFF]/60 tracking-wider uppercase mb-1.5">{item.subtitle}</div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function RustSection() {
  const benchmarks = [
    { label: '请求吞吐量', novalclaw: 98, openclaw: 1, unit: 'k req/s' },
    { label: '内存占用', novalclaw: 12, openclaw: 95, unit: 'MB', inverse: true },
    { label: '冷启动时间', novalclaw: 3, openclaw: 89, unit: 'ms', inverse: true },
    { label: 'P99 延迟', novalclaw: 5, openclaw: 82, unit: 'ms', inverse: true },
  ]

  return (
    <section id="rust" className="py-28 bg-[#08080a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(232,90,60,0.06),transparent_60%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6B8FFF]/[0.08] border border-[#6B8FFF]/20 mb-6">
              <Cpu className="w-3 h-3 text-[#6B8FFF]" />
              <span className="text-xs font-mono text-[#6B8FFF]/80 tracking-wider uppercase">Rust Powered</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              100% 纯 <span className="text-[#6B8FFF]">Rust</span> 构建
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed mb-8">
              不是 Python wrapper，不是 C++ binding。OmniNova 从推理引擎到网络层全部使用 Rust 原生实现，
              获得真正的零成本抽象、无 GC 停顿和内存安全保障。
            </p>

            <div className="space-y-3">
              {[
                { icon: Gauge, text: '零拷贝数据管线，内存带宽利用率 >95%' },
                { icon: Lock, text: 'Rust 所有权系统保障编译期内存安全' },
                { icon: Cog, text: 'Tokio 异步运行时，百万并发轻松应对' },
                { icon: Timer, text: '亚毫秒级延迟，P99 < 1ms' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-[#6B8FFF]/[0.08] flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-[#6B8FFF]" />
                  </div>
                  <span className="text-zinc-400">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Benchmark bars */}
          <div className="space-y-5">
            <div className="text-xs font-mono text-zinc-600 tracking-wider uppercase mb-6">Benchmark Comparison</div>
            {benchmarks.map((b, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-400">{b.label}</span>
                  <span className="text-xs font-mono text-zinc-600">{b.unit}</span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-[#6B8FFF] w-20 shrink-0">OmniNova</span>
                    <div className="flex-1 h-2.5 bg-white/[0.03] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#335EEA] to-[#6B8FFF] rounded-full transition-all duration-1000" style={{ width: `${b.inverse ? 100 - b.novalclaw : b.novalclaw}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-zinc-600 w-20 shrink-0">OpenClaw</span>
                    <div className="flex-1 h-2.5 bg-white/[0.03] rounded-full overflow-hidden">
                      <div className="h-full bg-zinc-700 rounded-full transition-all duration-1000" style={{ width: `${b.inverse ? 100 - b.openclaw : b.openclaw}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-8 p-4 rounded-xl border border-[#6B8FFF]/10 bg-[#6B8FFF]/[0.03]">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#6B8FFF] shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-white">性能领先 100 倍</div>
                  <div className="text-xs text-zinc-500">基于标准 benchmark 套件，同等硬件环境测试</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function MemorySection() {
  const memoryLayers = [
    {
      name: '工作记忆',
      nameEn: 'Working Memory',
      icon: Cpu,
      description: '当前对话的即时上下文，超低延迟访问。自动管理注意力窗口，智能压缩冗余信息。',
      specs: ['< 0.1ms 访问延迟', '动态窗口管理', '注意力权重优化'],
      color: '#6B8FFF',
    },
    {
      name: '长期记忆',
      nameEn: 'Long-term Memory',
      icon: HardDrive,
      description: '持久化的知识存储，支持百万级 token 持久化。基于向量索引的高效检索，永不遗忘。',
      specs: ['百万 token 持久化', '向量索引检索', '自动知识整合'],
      color: '#8BA4FF',
    },
    {
      name: '语义记忆',
      nameEn: 'Semantic Memory',
      icon: Network,
      description: '深层语义理解网络，构建概念关联图谱。跨会话的知识推理与迁移能力。',
      specs: ['知识图谱构建', '跨会话推理', '概念关联映射'],
      color: '#8BA4FF',
    },
  ]

  return (
    <section id="memory" className="py-28 bg-[#0A0D14] relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,rgba(255,159,122,0.04),transparent_60%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8BA4FF]/[0.08] border border-[#8BA4FF]/20 mb-6">
            <Brain className="w-3 h-3 text-[#8BA4FF]" />
            <span className="text-xs font-mono text-[#8BA4FF]/80 tracking-wider uppercase">Memory System</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            超强<span className="text-[#8BA4FF]">记忆</span>系统
          </h2>
          <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
            三层分级记忆架构，让智能体真正理解、记住、推理。<br />
            99% 记忆召回准确率，无限上下文窗口。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {memoryLayers.map((layer, i) => (
            <div key={i} className="group relative rounded-2xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-500 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ color: layer.color }} />
              <div className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${layer.color}15` }}>
                    <layer.icon className="w-5 h-5" style={{ color: layer.color }} />
                  </div>
                  <div>
                    <div className="text-white font-bold text-base">{layer.name}</div>
                    <div className="text-[10px] font-mono tracking-wider uppercase" style={{ color: `${layer.color}99` }}>{layer.nameEn}</div>
                  </div>
                </div>

                <p className="text-zinc-500 text-sm leading-relaxed mb-5">{layer.description}</p>

                <div className="space-y-2">
                  {layer.specs.map((spec, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: layer.color }} />
                      <span className="text-zinc-400">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Memory flow visualization */}
        <div className="mt-12 p-6 sm:p-8 rounded-2xl border border-white/[0.04] bg-white/[0.015]">
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#335EEA] to-[#6B8FFF] flex items-center justify-center">
                <Infinity className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-lg font-bold text-white mb-1">无限上下文窗口</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">
                传统框架受限于固定 context window，OmniNova 通过三层记忆架构实现等效无限上下文。
                工作记忆处理即时交互，长期记忆持久化关键信息，语义记忆构建知识网络——三者协同，让智能体永不遗忘。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function PredictionSection() {
  const pillars = [
    {
      icon: Clock,
      title: '时序模式识别',
      description: '从海量历史数据中提取时间序列规律，识别周期性模式、趋势拐点与异常信号。Rust 引擎在亿级数据点上实现实时模式匹配。',
      color: '#6B8FFF',
    },
    {
      icon: GitBranch,
      title: '多路径推演',
      description: '基于当前状态同时推演多条未来路径，评估每条路径的概率与影响。帮助决策者看清所有可能性，而不是赌一个方向。',
      color: '#6B8FFF',
    },
    {
      icon: Radar,
      title: '弱信号捕捉',
      description: '多模态感知配合超强记忆，能捕捉到传统分析遗漏的微弱信号——一条社交媒体情绪波动、一个供应链异常数据点、一次细微的用户行为偏移。',
      color: '#8BA4FF',
    },
    {
      icon: Lightbulb,
      title: '因果推理引擎',
      description: '不止于相关性，OmniNova 构建因果推理图谱，理解"为什么"而非仅仅"是什么"。从根因出发推导未来走势，预测更准确、更可解释。',
      color: '#8BA4FF',
    },
  ]

  const scenarios = [
    { icon: TrendingUp, label: '金融', example: '提前 72h 预警市场异动，识别黑天鹅事件前兆信号' },
    { icon: Crosshair, label: '安全', example: '预判攻击路径，在威胁行为发生前自动部署防御策略' },
    { icon: Heart, label: '陪护', example: '感知情绪趋势变化，在用户低落前主动给予关怀与支持' },
  ]

  return (
    <section id="prediction" className="py-28 bg-[#08080a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(232,90,60,0.05),transparent_60%)]" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[radial-gradient(circle,rgba(255,159,122,0.03),transparent_70%)]" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Content */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6B8FFF]/[0.08] border border-[#6B8FFF]/20 mb-6">
              <Telescope className="w-3 h-3 text-[#6B8FFF]" />
              <span className="text-xs font-mono text-[#6B8FFF]/80 tracking-wider uppercase">Predictive Intelligence</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              不只是响应，<br />更是<span className="text-[#6B8FFF]">预见</span>
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed mb-8">
              传统 AI 等待指令再行动。OmniNova 的预测智能引擎融合超强记忆系统与多模态感知，
              能从历史模式中学习规律，从实时信号中捕捉趋势，在事件发生之前给出预判。
            </p>

            {/* Core metric */}
            <div className="p-5 rounded-xl border border-[#6B8FFF]/15 bg-[#6B8FFF]/[0.04] mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#335EEA] to-[#6B8FFF] flex items-center justify-center shrink-0">
                  <LineChart className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-sm text-zinc-400 mb-0.5">预测准确率</div>
                  <div className="text-3xl font-black font-mono text-[#6B8FFF]">92.7%</div>
                  <div className="text-[10px] text-zinc-600">基于多行业回测验证</div>
                </div>
              </div>
            </div>

            {/* Application scenarios */}
            <div className="space-y-2.5">
              <div className="text-[11px] font-mono text-zinc-600 tracking-wider uppercase mb-3">预测场景示例</div>
              {scenarios.map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-white/[0.04] bg-white/[0.015]">
                  <div className="w-7 h-7 rounded-md bg-[#6B8FFF]/[0.08] flex items-center justify-center shrink-0 mt-0.5">
                    <s.icon className="w-3.5 h-3.5 text-[#6B8FFF]" />
                  </div>
                  <div>
                    <span className="text-xs font-mono text-[#6B8FFF]/70 tracking-wider">{s.label}</span>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-0.5">{s.example}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Four pillars */}
          <div className="space-y-4">
            {pillars.map((p, i) => (
              <div key={i} className="group relative rounded-2xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-500 overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ backgroundColor: p.color }} />
                <div className="p-6 sm:p-7">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: `${p.color}12` }}>
                      <p.icon className="w-5 h-5" style={{ color: p.color }} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white mb-2">{p.title}</h3>
                      <p className="text-zinc-500 text-sm leading-relaxed">{p.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Bottom insight */}
            <div className="p-5 rounded-xl border border-dashed border-white/[0.06] bg-white/[0.01] text-center">
              <p className="text-xs text-zinc-600 leading-relaxed">
                <span className="text-zinc-400 font-medium">记忆 + 多模态 + Rust 性能 = 预测智能</span><br />
                超强记忆提供历史深度，多模态提供感知广度，Rust 引擎提供实时推演速度——三者融合，让 OmniNova 真正具备预见未来的能力。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function MultimodalSection() {
  const modalities = [
    { icon: MessageCircle, name: '文本', desc: '自然语言理解与生成', color: '#6B8FFF' },
    { icon: FileImage, name: '图像', desc: '视觉识别与图像生成', color: '#6B8FFF' },
    { icon: AudioWaveform, name: '语音', desc: '语音识别与合成', color: '#8BA4FF' },
    { icon: Globe, name: '视频', desc: '视频理解与分析', color: '#8BA4FF' },
    { icon: FileCode, name: '代码', desc: '多语言代码推理', color: '#335EEA' },
    { icon: Database, name: '文档', desc: '结构化文档解析', color: '#6B8FFF' },
  ]

  return (
    <section id="multimodal" className="py-28 bg-[#08080a] relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(232,90,60,0.04),transparent_60%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.06] mb-6">
            <Eye className="w-3 h-3 text-[#6B8FFF]" />
            <span className="text-xs font-mono text-zinc-500 tracking-wider uppercase">Multimodal</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            原生<span className="text-[#6B8FFF]">多模态</span>引擎
          </h2>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto">
            六大模态原生融合，统一推理管线，跨模态零损耗
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {modalities.map((m, i) => (
            <div key={i} className="group relative rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-300 p-5 text-center">
              <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(circle at center, ${m.color}08, transparent)` }} />
              <div className="relative">
                <div className="w-10 h-10 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: `${m.color}12` }}>
                  <m.icon className="w-5 h-5" style={{ color: m.color }} />
                </div>
                <div className="text-sm font-semibold text-white mb-1">{m.name}</div>
                <div className="text-[11px] text-zinc-600 leading-tight">{m.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Cross-modal fusion */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/[0.04] bg-white/[0.015] p-5 text-center">
            <div className="text-2xl font-black font-mono text-[#6B8FFF] mb-1">0</div>
            <div className="text-xs text-zinc-500">跨模态切换损耗</div>
          </div>
          <div className="rounded-xl border border-white/[0.04] bg-white/[0.015] p-5 text-center">
            <div className="text-2xl font-black font-mono text-white mb-1">统一</div>
            <div className="text-xs text-zinc-500">推理管线架构</div>
          </div>
          <div className="rounded-xl border border-white/[0.04] bg-white/[0.015] p-5 text-center">
            <div className="text-2xl font-black font-mono text-white mb-1">实时</div>
            <div className="text-xs text-zinc-500">多模态融合推理</div>
          </div>
        </div>
      </div>
    </section>
  )
}

function SecuritySection() {
  const layers = [
    { name: 'Rust 内存安全', desc: '编译期内存安全，杜绝 CVE', icon: Lock },
    { name: 'Prompt 注入防护', desc: '多层过滤，智能检测恶意输入', icon: Shield },
    { name: '数据沙箱隔离', desc: '每个智能体运行在独立沙箱中', icon: Database },
    { name: '端到端加密', desc: 'TLS 1.3 + AES-256 全链路加密', icon: Lock },
    { name: '越狱防御系统', desc: '深度语义分析，识别绕过攻击', icon: ShieldCheck },
    { name: '审计追踪', desc: '全量操作日志，满足合规审计', icon: Search },
  ]

  return (
    <section id="security" className="py-28 bg-[#0A0D14] relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,74,44,0.05),transparent_60%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: security layers */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {layers.map((layer, i) => (
                <div key={i} className="group flex items-start gap-3 p-4 rounded-xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.03] hover:border-[#6B8FFF]/15 transition-all duration-300">
                  <div className="w-9 h-9 rounded-lg bg-[#6B8FFF]/[0.08] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <layer.icon className="w-4 h-4 text-[#6B8FFF]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white mb-0.5">{layer.name}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{layer.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#335EEA]/[0.08] border border-[#335EEA]/20 mb-6">
              <Shield className="w-3 h-3 text-[#6B8FFF]" />
              <span className="text-xs font-mono text-[#6B8FFF]/80 tracking-wider uppercase">Security</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              <span className="text-[#6B8FFF]">军事级</span>安全防护
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed mb-8">
              安全不是附加功能，而是 OmniNova 的基因。Rust 语言本身提供内存安全保障，
              框架层面叠加六层防护体系，从 Prompt 注入到数据泄露，全面守护企业数据资产。
            </p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'SOC2', desc: 'Type II' },
                { name: 'ISO27001', desc: '信息安全' },
                { name: '等保三级', desc: '等级保护' },
                { name: 'GDPR', desc: '数据合规' },
              ].map((cert, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-white/[0.04] bg-white/[0.015]">
                  <CheckCircle2 className="w-4 h-4 text-[#6B8FFF] shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-white">{cert.name}</div>
                    <div className="text-[10px] text-zinc-600">{cert.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function CompareSection() {
  const rows = [
    { feature: '核心语言', nc: 'Rust', oc: 'Python', highlight: true },
    { feature: '推理性能', nc: '100x 基准', oc: '1x 基准', highlight: true },
    { feature: '记忆系统', nc: '三层分级记忆', oc: '固定上下文窗口', highlight: true },
    { feature: '内存安全', nc: '编译期保障', oc: '运行时检查', highlight: true },
    { feature: '多模态', nc: '6 模态原生融合', oc: '文本为主', highlight: false },
    { feature: '并发模型', nc: '百万级异步', oc: '线程池', highlight: false },
    { feature: '冷启动', nc: '< 3ms', oc: '> 200ms', highlight: false },
    { feature: '安全防护', nc: '六层纵深防御', oc: '基础防护', highlight: false },
  ]

  return (
    <section className="py-28 bg-[#08080a] relative">
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
            OmniNova vs OpenClaw
          </h2>
          <p className="text-zinc-500 text-base">
            全面对比，优势一目了然
          </p>
        </div>

        <div className="rounded-2xl border border-white/[0.04] overflow-hidden">
          <div className="grid grid-cols-3 border-b border-white/[0.06] bg-white/[0.02]">
            <div className="p-4"><span className="text-xs font-mono text-zinc-600 tracking-wider uppercase">Feature</span></div>
            <div className="p-4 text-center border-x border-white/[0.04]">
              <span className="text-xs font-mono text-[#6B8FFF] tracking-wider font-bold">OmniNova</span>
            </div>
            <div className="p-4 text-center">
              <span className="text-xs font-mono text-zinc-600 tracking-wider">OpenClaw</span>
            </div>
          </div>

          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 border-b border-white/[0.03] last:border-b-0 ${row.highlight ? 'bg-[#6B8FFF]/[0.02]' : ''}`}>
              <div className="p-4 flex items-center gap-2">
                <span className="text-sm text-zinc-300">{row.feature}</span>
              </div>
              <div className="p-4 text-center border-x border-white/[0.03]">
                <span className="text-sm font-medium text-[#8BA4FF]">{row.nc}</span>
              </div>
              <div className="p-4 text-center">
                <span className="text-sm text-zinc-600">{row.oc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function UseCasesSection() {
  const cases = [
    {
      id: 'finance',
      icon: TrendingUp,
      accentColor: '#6B8FFF',
      label: 'FinTech',
      title: '量化金融',
      subtitle: '更快的底层架构，让金融分析和交易决策领先一步',
      description: 'Rust 原生推理引擎提供亚毫秒级延迟，在高频交易场景中每微秒都意味着收益。OmniNova 的超强记忆系统可持久化海量市场数据与历史模式，让智能体在瞬息万变的市场中做出最优决策。',
      features: [
        { icon: Zap, text: '亚毫秒级推理，高频交易场景零延迟' },
        { icon: BarChart3, text: '多模态分析——财报、K线、新闻一体化处理' },
        { icon: Brain, text: '超强记忆持久化历史行情与交易模式' },
        { icon: Shield, text: '金融级安全合规，满足监管审计要求' },
      ],
      stats: [
        { value: '<0.5ms', label: '交易决策延迟' },
        { value: '100x', label: '回测速度提升' },
        { value: '99.9%', label: '系统可用性' },
      ],
    },
    {
      id: 'security',
      icon: Crosshair,
      accentColor: '#335EEA',
      label: 'CyberSec',
      title: '网络安全',
      subtitle: '速度更快更智能，自动化渗透测试的终极武器',
      description: '比传统方案快 100 倍的扫描引擎，配合多模态能力同时分析网络流量、日志文件和系统截图。记忆系统完整记录每次渗透路径与漏洞特征，智能体越用越聪明。',
      features: [
        { icon: Scan, text: '100x 扫描速度，大规模资产秒级发现' },
        { icon: Activity, text: '多模态威胁分析——流量、日志、截图联动' },
        { icon: Brain, text: '记忆系统积累漏洞知识库，越用越强' },
        { icon: Lock, text: 'Rust 内存安全，工具自身零漏洞风险' },
      ],
      stats: [
        { value: '100x', label: '扫描速度提升' },
        { value: '0', label: '自身 CVE 漏洞' },
        { value: '7x24h', label: '持续自动化测试' },
      ],
    },
    {
      id: 'companion',
      icon: Heart,
      accentColor: '#8BA4FF',
      label: 'EmotionAI',
      title: '情感陪护',
      subtitle: '配备情感计算引擎，每一次交互都有温度',
      description: '内置情感计算引擎，实时感知用户情绪变化。多模态能力让智能体能"听"语气、"看"表情、"懂"文字。超强记忆系统记住每一个用户的偏好、经历与情感轨迹，构建真正有温度的长期陪伴关系。',
      features: [
        { icon: Smile, text: '情感计算引擎，实时感知情绪波动' },
        { icon: Headphones, text: '语音语调分析，捕捉言语中的情感信号' },
        { icon: Brain, text: '长期记忆——记住每个人的故事与偏好' },
        { icon: MessagesSquare, text: '多轮深度对话，构建真实情感连接' },
      ],
      stats: [
        { value: '95%', label: '情绪识别准确率' },
        { value: '∞', label: '用户记忆深度' },
        { value: '6', label: '情感模态融合' },
      ],
    },
  ]

  return (
    <section id="usecases" className="py-28 bg-[#0A0D14] relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(232,90,60,0.04),transparent_60%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.06] mb-6">
            <Crosshair className="w-3 h-3 text-[#6B8FFF]" />
            <span className="text-xs font-mono text-zinc-500 tracking-wider uppercase">Core Applications</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
            核心<span className="text-[#6B8FFF]">应用</span>场景
          </h2>
          <p className="text-zinc-500 text-lg max-w-2xl mx-auto">
            从金融到安全到陪护，OmniNova 的极致性能与超强记忆<br className="hidden sm:block" />
            在每个场景中释放不同维度的价值
          </p>
        </div>

        <div className="space-y-6">
          {cases.map((c, idx) => (
            <div key={c.id} className="group rounded-2xl border border-white/[0.04] bg-white/[0.015] hover:bg-white/[0.025] hover:border-white/[0.08] transition-all duration-500 overflow-hidden">
              <div className="p-6 sm:p-8 lg:p-10">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${c.accentColor}15` }}>
                      <c.icon className="w-6 h-6" style={{ color: c.accentColor }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5">
                        <h3 className="text-2xl font-black text-white">{c.title}</h3>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full tracking-wider" style={{ backgroundColor: `${c.accentColor}12`, color: `${c.accentColor}cc` }}>{c.label}</span>
                      </div>
                      <p className="text-sm text-zinc-500 mt-0.5">{c.subtitle}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  {/* Left: Description + Features */}
                  <div className="lg:col-span-3">
                    <p className="text-zinc-400 text-sm leading-relaxed mb-6">{c.description}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {c.features.map((f, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-white/[0.02] border border-white/[0.03]">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${c.accentColor}10` }}>
                            <f.icon className="w-3.5 h-3.5" style={{ color: c.accentColor }} />
                          </div>
                          <span className="text-xs text-zinc-400 leading-relaxed">{f.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Stats */}
                  <div className="lg:col-span-2 flex flex-row lg:flex-col gap-3">
                    {c.stats.map((s, i) => (
                      <div key={i} className="flex-1 p-4 rounded-xl border border-white/[0.04] bg-white/[0.02] text-center">
                        <div className="text-xl sm:text-2xl font-black font-mono mb-0.5" style={{ color: c.accentColor }}>{s.value}</div>
                        <div className="text-[10px] text-zinc-600 tracking-wide">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CTASection() {
  return (
    <section className="py-28 bg-[#0A0D14] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,90,60,0.08),transparent_70%)]" />
      <div className="absolute inset-0 hero-grid opacity-[0.02]" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6B8FFF]/[0.08] border border-[#6B8FFF]/20 mb-8">
          <div className="w-1.5 h-1.5 rounded-full bg-[#6B8FFF] animate-pulse" />
          <span className="text-xs font-mono text-[#6B8FFF]/80 tracking-wider">READY FOR PRODUCTION</span>
        </div>

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
          体验 <span className="text-[#6B8FFF]">100x</span> 的飞跃
        </h2>
        <p className="text-zinc-500 text-lg mb-10 max-w-xl mx-auto">
          三行代码启动企业级安全智能体，从今天开始构建下一代 AI 应用
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <Button asChild size="lg" className="bg-[#335EEA] hover:bg-[#2748D4] text-white border-0 px-10 h-13 text-base font-medium shadow-lg shadow-[#335EEA]/20">
            <a href="/claw/">立即开始 <ArrowRight className="w-4 h-4 ml-2" /></a>
          </Button>
        </div>
      </div>
    </section>
  )
}

function Footer() {
  const links = {
    product: { title: '产品', items: ['快速开始', '更新日志'] },
    tech: { title: '技术', items: ['Rust 架构', '记忆系统', '多模态引擎', '安全白皮书', 'Benchmark'] },
    company: { title: '公司', items: ['关于我们', '博客', '招聘', '联系我们'] },
    legal: { title: '法律', items: ['隐私政策', '服务条款', '安全策略'] },
  }

  return (
    <footer className="bg-[#0A0D14] border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <ImageComponent src="/logo.png" alt="OmniNova" width={28} height={28} className="rounded-lg" />
              <span className="text-base font-bold text-white tracking-tight">OmniNova</span>
            </div>
            <p className="text-zinc-600 text-xs leading-relaxed mb-4">
              Rust 构建的企业级<br />安全智能体
            </p>
            <div className="flex gap-3">
              <a href="#" className="text-zinc-700 hover:text-[#6B8FFF] transition-colors"><Twitter className="w-4 h-4" /></a>
              <a href="#" className="text-zinc-700 hover:text-[#6B8FFF] transition-colors"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>

          {Object.values(links).map((section, i) => (
            <div key={i}>
              <h4 className="text-xs font-mono text-zinc-500 tracking-wider uppercase mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.items.map((item, j) => (
                  <li key={j}><a href={item === '快速开始' ? '/claw/' : '#'} className="text-zinc-600 hover:text-zinc-300 transition-colors text-sm">{item}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/[0.03] flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="text-zinc-700 text-xs">&copy; 2025 OmniNova. All rights reserved.</div>
          <div className="text-zinc-700 text-xs font-mono">Built with Rust. Secured by design.</div>
        </div>
      </div>
    </footer>
  )
}

export default function Home() {
  return (
    <main className="min-h-screen bg-[#0A0D14]">
      <Navigation />
      <HeroSection />
      <AdvantagesSection />
      <RustSection />
      <MemorySection />
      <PredictionSection />
      <MultimodalSection />
      <SecuritySection />
      <CompareSection />
      <UseCasesSection />
      <CTASection />
      <Footer />
    </main>
  )
}
